import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react"
import type { TRadioTrack } from "@/types"
import { radioAPI } from "@/utils"

interface IRadioPlayerState {
  queue: TRadioTrack[]
  currentIndex: number | null
  currentTrack: TRadioTrack | null
  isPlaying: boolean
  isExpanded: boolean
}

interface IRadioPlayerActions {
  play: (track: TRadioTrack) => void
  playAt: (index: number) => void
  resume: () => void
  pause: () => void
  next: () => void
  prev: () => void
  toggleExpand: () => void
  close: () => void
  setQueue: (tracks: TRadioTrack[]) => void
}

type TRadioPlayerContext = IRadioPlayerState & IRadioPlayerActions

const RadioPlayerContext = createContext<TRadioPlayerContext | null>(null)

export function RadioPlayerProvider({ children }: { children: ReactNode }) {
  const [queue, setQueueState] = useState<TRadioTrack[]>([])
  const [currentIndex, setCurrentIndex] = useState<number | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const currentTrackIdRef = useRef<string | null>(null)

  const play = useCallback((track: TRadioTrack) => {
    const prev = queueRef.current
    const index = prev.findIndex((t) => t.id === track.id)

    if (index >= 0) {
      setCurrentIndex(index)
    } else {
      setQueueState((s) => [...s, track])
      setCurrentIndex(prev.length)
    }
    setIsPlaying(true)
  }, [])

  const playAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= queue.length) return

      setCurrentIndex(index)
      setIsPlaying(true)
    },
    [queue.length],
  )

  const resume = useCallback(() => {
    setIsPlaying(currentIndex !== null)
  }, [currentIndex])

  const pause = useCallback(() => {
    setIsPlaying(false)
  }, [])

  const next = useCallback(() => {
    setCurrentIndex((index) => {
      if (index === null || queue.length === 0) return index

      setIsPlaying(true)
      return index + 1 < queue.length ? index + 1 : 0
    })
  }, [queue.length])

  const prev = useCallback(() => {
    setCurrentIndex((index) => {
      if (index === null || queue.length === 0) return index

      setIsPlaying(true)
      return index - 1 >= 0 ? index - 1 : queue.length - 1
    })
  }, [queue.length])

  const toggleExpand = useCallback(() => {
    setIsExpanded((p) => !p)
  }, [])

  const close = useCallback(() => {
    setCurrentIndex(null)
    setIsPlaying(false)
    setIsExpanded(false)
  }, [])

  useEffect(() => {
    currentTrackIdRef.current = currentIndex !== null ? (queue[currentIndex]?.id ?? null) : null
  }, [currentIndex, queue])

  const isPlayingRef = useRef(isPlaying)
  isPlayingRef.current = isPlaying
  const queueRef = useRef(queue)
  queueRef.current = queue

  const setQueue = useCallback((tracks: TRadioTrack[]) => {
    const currentTrackId = currentTrackIdRef.current

    // If playing and current track is missing from new queue, keep it at the front
    if (isPlayingRef.current && currentTrackId) {
      const hasCurrent = tracks.some((t) => t.id === currentTrackId)
      if (!hasCurrent) {
        const current = queueRef.current.find((t) => t.id === currentTrackId)
        if (current) {
          setQueueState([current, ...tracks])
          setCurrentIndex(0)
          return
        }
      }
    }

    setQueueState(tracks)

    if (!currentTrackId) return

    const newIndex = tracks.findIndex((t) => t.id === currentTrackId)

    if (newIndex >= 0) {
      setCurrentIndex(newIndex)
      return
    }

    setCurrentIndex(null)
    setIsPlaying(false)
  }, [])

  const currentTrack = queue[currentIndex ?? -1] ?? null

  // Notify backend when a track starts playing (only on track change)
  const lastNotifiedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isPlaying || !currentTrack) return
    if (lastNotifiedRef.current === currentTrack.id) return
    lastNotifiedRef.current = currentTrack.id
    radioAPI.notifyPlay(currentTrack.id)
  }, [isPlaying, currentTrack])

  return (
    <RadioPlayerContext.Provider
      value={{
        queue,
        currentIndex,
        currentTrack,
        isPlaying,
        isExpanded,
        play,
        playAt,
        resume,
        pause,
        next,
        prev,
        toggleExpand,
        close,
        setQueue,
      }}
    >
      {children}
    </RadioPlayerContext.Provider>
  )
}

export function useRadioPlayer(): TRadioPlayerContext {
  const context = useContext(RadioPlayerContext)
  const { isPlaying, currentTrack, resume, pause, next, prev, close } = context || {}

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return
    const mediaSession = navigator.mediaSession
    if (!currentTrack) return clearMediaSession()

    const metadata: MediaMetadataInit = {
      title: currentTrack.title ?? currentTrack.url,
      artist: currentTrack.artist ?? "",
    }

    if (currentTrack.thumbnail) {
      metadata.artwork = [{ src: currentTrack.thumbnail, sizes: "96x96", type: "image/jpeg" }]
    }

    mediaSession.metadata = new MediaMetadata(metadata)
    mediaSession.setActionHandler("play", resume!)
    mediaSession.setActionHandler("pause", pause!)
    mediaSession.setActionHandler("nexttrack", next!)
    mediaSession.setActionHandler("previoustrack", prev!)
    mediaSession.setActionHandler("stop", close!)

    return clearMediaSession
  }, [currentTrack, isPlaying])

  if (!context) {
    throw new Error("useRadioPlayer must be used within RadioPlayerProvider")
  }

  return context
}

function clearMediaSession() {
  if (!("mediaSession" in navigator)) return

  navigator.mediaSession.setActionHandler("play", null)
  navigator.mediaSession.setActionHandler("pause", null)
  navigator.mediaSession.setActionHandler("nexttrack", null)
  navigator.mediaSession.setActionHandler("previoustrack", null)
  navigator.mediaSession.setActionHandler("seekforward", null)
  navigator.mediaSession.setActionHandler("seekbackward", null)
  navigator.mediaSession.setActionHandler("stop", null)

  navigator.mediaSession.metadata = null
  navigator.mediaSession.playbackState = "none"
}
